import { Dumbbell } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/common/ui/table";

import type { WhoopWorkout } from "../devices-types";

interface WorkoutsCardProps {
  workouts: WhoopWorkout[];
  isLoading: boolean;
}

const SPORT_NAMES: Record<number, string> = {
  0: "Running",
  1: "Running",
  33: "Cycling",
  44: "Walking",
  48: "Swimming",
  52: "Yoga",
  63: "HIIT",
  71: "Functional Fitness",
  82: "Pilates",
  84: "Rowing",
};

function formatDurationMinutes(start: string, end: string): string {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return `${Math.round(ms / 60_000)}m`;
}

function formatCalories(kilojoule: number): string {
  return `${Math.round(kilojoule / 4.184)} cal`;
}

export function WorkoutsCard({ workouts, isLoading }: WorkoutsCardProps): React.ReactElement {
  return (
    <Card data-testid="workouts-card" className="col-span-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Recent Workouts</CardTitle>
        <Dumbbell className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : workouts.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Activity</TableHead>
                <TableHead>Strain</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Avg HR</TableHead>
                <TableHead>Max HR</TableHead>
                <TableHead>Calories</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workouts.map((workout) => (
                <TableRow key={workout.id} data-testid={`workout-row-${workout.id}`}>
                  <TableCell className="font-medium">
                    {SPORT_NAMES[workout.sport_id] ?? `Sport ${workout.sport_id}`}
                  </TableCell>
                  <TableCell>{workout.score.strain.toFixed(1)}</TableCell>
                  <TableCell>{formatDurationMinutes(workout.start, workout.end)}</TableCell>
                  <TableCell>{workout.score.average_heart_rate} bpm</TableCell>
                  <TableCell>{workout.score.max_heart_rate} bpm</TableCell>
                  <TableCell>{formatCalories(workout.score.kilojoule)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground">No workout data available</p>
        )}
      </CardContent>
    </Card>
  );
}
